import { inject, Injectable } from "@angular/core";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { TestLog } from "@bitwarden/web-vault/app/services/syncing/test.log";
import { SyncStatusEnum } from "@bitwarden/web-vault/app/models/types/general-types";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { GlossSyncService } from "@bitwarden/web-vault/app/services/syncing/gloss.sync.service";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { dateDiffWithoutTime } from "@bitwarden/web-vault/app/shared/utils/helper.date/date-comparison";
import { SourceTransaction } from "@bitwarden/web-vault/app/models/data/blobby/source-transaction.data";
import { potentialTransaction } from "@bitwarden/web-vault/app/validators/base-validator";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";

@Injectable({
  providedIn: "root",
})
export class BasiqTransactionValidator {
  private transactionStore: TransactionStoreService;
  private glossSyncService: GlossSyncService;
  private testLogService: TestLog;
  private appState: AppStateStoreService;
  validatedTransactions: TransactionView[];
  syncStatus: SyncStatusEnum;

  constructor() {
    this.transactionStore = inject(TransactionStoreService);
    this.glossSyncService = inject(GlossSyncService);
    this.appState = inject(AppStateStoreService);
    this.testLogService = new TestLog(inject(LogService));
  }
  async validateTransactions(transactions: TransactionView[], account: AccountView) {
    if (transactions.length) {
      try {
        const validatedTransactions = transactions.map((transaction) => {
          return this.validateTransaction(transaction);
        });

        if (!validatedTransactions) {
          await this.glossSyncService.terminateSync(
            [account],
            SyncStatusEnum.cantValidateTransactions,
          );
        }

        return validatedTransactions.filter((transaction) => !transaction.isDuplicate);
      } catch (error) {
        this.testLogService.testLog({
          level: "Validating transactions against duplication",
          account: account.name,
          message: "Could not validate transactions",
          reasons: "buggy validation code",
          error: error,
        });
        await this.glossSyncService.terminateSync(
          [account],
          SyncStatusEnum.cantValidateTransactions,
        );
      }
    }

    this.testLogService.testLog({
      level: "Validating transactions against duplication",
      account: account.name,
      message: "Could not validate transactions",
      reasons: "no transactions to validate",
    });
    await this.glossSyncService.terminateSync([account], SyncStatusEnum.noTransactionsToValidate);
  }

  async validate(transactions: TransactionView[], account: AccountView) {
    try {
      if (!transactions?.length) {
        return true;
      }

      const processMessage = `Validating Transactions of ${account.name}`;
      this.appState.startProcess("importing-data", processMessage);
      this.validatedTransactions = await this.validateTransactions(transactions, account);
      const isValidated = !!this.validatedTransactions;
      this.appState.endProcess("importing-data");
      return isValidated;
    } catch (error) {
      await this.glossSyncService.logError(
        [account],
        error,
        "Validating your transactions",
        SyncStatusEnum.cantValidateTransactions,
      );
      this.syncStatus = SyncStatusEnum.cantValidateTransactions;
      this.appState.endProcess("importing-data");
      return false;
    }
  }

  validateTransaction(transaction: TransactionView): TransactionView {
    transaction.isDuplicate = this.isDuplicate(transaction);
    return transaction;
  }

  isDuplicate(item: TransactionView) {
    const existingTransactions = this.transactionStore.enrichTransaction.collection();

    const sameIdTransaction = existingTransactions.some((t) => t.id === item.id);
    if (sameIdTransaction) {
      return true;
    }

    /** If the amount is a new amount then this is a new transaction */
    const isSameAmount = existingTransactions.filter((et) => {
      return et.quantity.actualQuantity.amount === item.quantity.actualQuantity.amount;
    });

    if (!isSameAmount.length) {
      return false;
    }

    /** if there is any transactions of the same date and amount*/
    const isSameDate = isSameAmount.filter((et) => {
      return dateDiffWithoutTime(et.postDate, item.postDate) === 0;
    });

    if (!isSameDate.length) {
      return false;
    }

    /** if there is any transactions of the same account, amount and date*/
    const isSameAccount = isSameDate.filter((et) => {
      return et.accountLink.id === item.accountLink.id;
    });

    if (!isSameAccount.length) {
      return false;
    }

    /** if there is any transactions of the same account, amount and date*/
    const isSameDescription = isSameAccount.filter((et) => {
      return et.description === item.description;
    });

    return !!isSameDescription.length;
  }

  /** sourceTransaction and transaction are saved on the same account when coming from Basiq */
  async checkAccountDuplicity(
    existingTransaction: SourceTransaction,
    transaction: potentialTransaction,
  ) {
    return existingTransaction.accountId === transaction.accountId;
  }
}
